import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import {
  BudgetClassificationCodeFormDto,
  BudgetClassificationCodeFormInterface
} from '@zaoblako/shared/modules/catalog/dtos/budget-classification-code/budget-classification-code-form.dto';
import { BudgetClassificationCodeFindInterface } from '@zaoblako/shared/modules/catalog/dtos/budget-classification-code/budget-classification-code-find.dto';
import { ConfirmDialogService } from '@zaoblako/web/core/services/confirm-dialog.service';
import { ErrorService } from '@zaoblako/web/core/services/error.service';
import { BudgetClassificationCodeService } from '@zaoblako/web/core/services/basis/budget-classification-code.service';
import { FormType } from '@zaoblako/web/shared/components/form-sidebar/models/form-state';
import { FormStoreService } from '@zaoblako/web/shared/components/form-sidebar/services/form-store.service';
import { BaseFormComponent } from '@zaoblako/web/shared/dumbs/base-form/base-form.component';
import { InputTextComponent } from '@zaoblako/web/shared/dumbs/input-text/input-text.component';
import { BudgetRevenueAdminSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/budget-revenue-admin-single-select/budget-revenue-admin-single-select.component';
import { SpinnerComponent } from '@zaoblako/web/shared/dumbs/spinner/spinner.component';
import { JoinPipe } from '@zaoblako/web/shared/pipes/join.pipe';
import { ButtonComponent } from '@zaoblako/web/shared/dumbs/button/button.component';
import { CardModule } from 'primeng/card';
import { TagModule } from 'primeng/tag';
import { Observable } from 'rxjs';

@Component({
  standalone: true,
  selector: 'crm-budget-classification-code-form',
  templateUrl: './budget-classification-code-form.component.html',
  imports: [
    CommonModule,
    CardModule,
    InputTextComponent,
    JoinPipe,
    BudgetRevenueAdminSingleSelectComponent,
    ButtonComponent,
    SpinnerComponent,
    TagModule
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class BudgetClassificationCodeFormComponent
  extends BaseFormComponent<BudgetClassificationCodeFormInterface>
  implements OnInit {
  @Input() public readonly index: number;
  public code = <BudgetClassificationCodeFormInterface>{};
  public loading$: Observable<boolean>;

  public dto = BudgetClassificationCodeFormDto;
  public form$: Observable<FormType>;

  public constructor(
    private readonly budgetClassificationCodeService: BudgetClassificationCodeService,
    private readonly confirmService: ConfirmDialogService,
    public readonly formStoreService: FormStoreService,
    private readonly errorService: ErrorService,
    private readonly cdRef: ChangeDetectorRef
  ) {
    super();
  }

  public ngOnInit(): void {
    this.form$ = this.formStoreService.select((state) => state.forms[this.index]);
    this.loading$ = this.formStoreService.select((state) => state.forms[this.index]?.loading);

    if (this.formStoreService.getState.forms[this.index]?.isEdit) {
      this.getById();
    }
  }

  public getById(): void {
    this.formStoreService.setLoading(true, this.index);

    this.budgetClassificationCodeService
      .findById<BudgetClassificationCodeFindInterface>(this.formStoreService.getState.forms[this.index]?.entityId, {
        withDeleted: true
      })
      .subscribe({
        next: (code) => {
          this.code = code;
          this.formStoreService.setLoading(false, this.index);
          this.cdRef.markForCheck();
        }
      });
  }

  public saveCode(): void {
    if (this.checkValidate(this.code).valid) {
      if (this.formStoreService.getState.forms[this.index]?.entityId) {
        this.formStoreService.setLoading(true, this.index);

        this.budgetClassificationCodeService
          .update<BudgetClassificationCodeFormInterface, BudgetClassificationCodeFindInterface>(this.code)
          .subscribe({
            next: (code) => {
              this.formStoreService.setLoading(false, this.index);
              this.errorService.addSuccessMessage(`КБК "${this.code.name}" обновлен!`);
              this.formStoreService.saveNext(code, this.index);
            },
            error: () => {
              this.formStoreService.setLoading(false, this.index);
            }
          });
      } else {
        this.formStoreService.setLoading(true, this.index);

        this.budgetClassificationCodeService.create<BudgetClassificationCodeFindInterface>(this.code).subscribe({
          next: (code) => {
            this.formStoreService.setLoading(false, this.index);
            this.errorService.addSuccessMessage('КБК создан!');
            this.formStoreService.saveNext(code, this.index);
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
          }
        });
      }
    }
  }

  public deleteCode(): void {
    this.confirmService.confirm({
      message: `Вы действительно хотите удалить КБК "${this.code.name}"?`,
      accept: () => {
        this.formStoreService.setLoading(true, this.index);
        this.cdRef.markForCheck();

        this.budgetClassificationCodeService
          .deleteById(this.formStoreService.getState.forms[this.index]?.entityId)
          .subscribe({
            next: () => {
              this.errorService.addSuccessMessage(`КБК "${this.code.name}" удален!`);
              this.formStoreService.deleteNext(this.index);
            },
            error: () => {
              this.formStoreService.setLoading(false, this.index);
            }
          });
      }
    });
  }

  public restoreCode(): void {
    this.confirmService.confirm({
      message: `Вы действительно хотите восстановить КБК "${this.code?.name}"?`,
      accept: () => {
        this.formStoreService.setLoading(true, this.index);
        this.cdRef.markForCheck();

        this.budgetClassificationCodeService
          .restore(this.formStoreService.getState.forms[this.index]?.entityId)
          .subscribe({
            next: () => {
              this.code = { ...this.code, deleted: null } as BudgetClassificationCodeFindInterface;
              this.cdRef.markForCheck();
              this.errorService.addSuccessMessage(`КБК "${this.code?.name}" восстановлен!`);
              this.formStoreService.saveNext(this.code, this.index);
            },
            error: () => {
              this.formStoreService.setLoading(false, this.index);
            }
          });
      }
    });
  }
}
